import { Location, PlatformLocation } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot } from "@angular/router";
import { RouterLibCommand } from "@fabric-types/lib-router";
import { FetchStatusType } from "@obg/ngrx-utils";
import { DocumentRef, ignoreEmpty } from "@obg/ngx.global";
import { Log, LoggingFactory } from "@obg/ngx.global.evo";
import { flatMap, isEqual, isNil, map as lodashMap, startsWith, uniq } from "lodash-es";
import { Observable, of, Subscription, firstValueFrom } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { RouteConfig, ROUTER_CONFIG, RouterConfig } from "../config/router-config.model";
import { LazyLoadingService } from "../lazy-loading/lazy-loading.service";

import {
	NavigateByCommandPayload,
	NavigateByUrlPayload,
	NavigatePayload,
	SetFragmentPayload,
	SetQueryParamsPayload,
} from "./route.model";
import { getActivatedRouteSnapshot, getAllParents } from "./route.util";

@Injectable({
	providedIn: "root",
})
export class RouteService {
	private routeLookup: Record<string, RouteConfig> = {};
	private logger: Log;
	private parentLookup: Record<string, RouteConfig | undefined> = {};
	private lazyLoadingEvents$$: Subscription | undefined;

	constructor(
		private documentRef: DocumentRef,
		private router: Router,
		private location: Location,
		private lazyLoadingService: LazyLoadingService,
		private platformLocation: PlatformLocation,
		@Inject(ROUTER_CONFIG) private config: RouterConfig,
		loggingFactory: LoggingFactory
	) {
		this.logger = loggingFactory.get("routeService");
	}

	/** @internal */
	initialize() {
		if (this.lazyLoadingEvents$$) {
			return;
		}

		const addToLookups = (routeConfig: RouteConfig, parentConfig?: RouteConfig) => {
			if (this.routeLookup[routeConfig.name]) {
				if (!routeConfig.path) {
					this.routeLookup[routeConfig.name] = {
						...routeConfig,
						path: this.routeLookup[routeConfig.name].path,
					};
					return;
				}
				if (!isEqual(this.routeLookup[routeConfig.name], routeConfig)) {
					throw new Error(`[RouteService] registering a route '${routeConfig.name}' which already exists!`);
				}
			}
			this.routeLookup[routeConfig.name] = routeConfig;
			this.parentLookup[routeConfig.name] = parentConfig;
		};

		this.walkRouteConfig(this.router.config, addToLookups);

		this.lazyLoadingEvents$$ = this.lazyLoadingService.getLazyLoadSuccess$().subscribe(
			(payload) => this.walkRouteConfig(payload.loadedChildren, addToLookups, payload.parentRoute),
			(err) => this.logger.error("Lazy loading", err)
		);

		setTimeout(() => {
			this.documentRef.obgEventBus?.listen(RouterLibCommand.LegacyRouterNavigateByParam, (payload) =>
				this.navigate({ routeName: payload.param })
			);
		}, 0);
	}

	/** @internal */
	dispose() {
		if (!this.lazyLoadingEvents$$) {
			return;
		}

		this.lazyLoadingEvents$$.unsubscribe();
		this.lazyLoadingEvents$$ = undefined;
	}

	setQueryParams(payload: SetQueryParamsPayload, mergeQueryParams = false): Promise<boolean> {
		const { queryParams, replaceUrl, extras } = payload;
		const queryParamsHandling = mergeQueryParams ? "merge" : "";

		return this.router.navigate([], {
			queryParams,
			queryParamsHandling,
			replaceUrl,
			...extras,
		});
	}

	setFragment(payload: SetFragmentPayload): Promise<boolean> {
		const { fragment, replaceUrl } = payload;

		return this.router.navigate([], {
			replaceUrl,
			fragment: fragment ? fragment : undefined,
			queryParamsHandling: "preserve",
		});
	}

	/**
	 * Returns a promise that:
	 * - resolves to 'true' when navigation succeeds,
	 * - resolves to 'false' when navigation fails,
	 * - is rejected when an error happens.
	 */
	navigate(payload: NavigatePayload): Promise<boolean> {
		return firstValueFrom(
			this.getRouteConfig$(payload.routeName).pipe(
				map(
					() =>
						this.convertToCommands(payload.routeName, payload.params) ||
						this.convertToCommands(this.config.unknownRoute)
				),
				switchMap((commands) =>
					this.navigateByCommand({
						commands: commands!,
						extras: payload.extras,
					})
				)
			)
		);
	}

	/**
	 * Returns a promise that:
	 * - resolves to 'true' when navigation succeeds,
	 * - resolves to 'false' when navigation fails,
	 * - is rejected when an error happens.
	 */
	navigateByCommand(payload: NavigateByCommandPayload): Promise<boolean> {
		return this.router.navigate(payload.commands, payload.extras);
	}

	navigateByRedirect(payload: NavigatePayload): Promise<FetchStatusType | undefined> {
		return firstValueFrom(
			this.getRouteConfig$(payload.routeName).pipe(
				tap(() => (this.documentRef.native.location!.href = this.generateUrl(payload, true))),
				map((config) => config && FetchStatusType.Success)
			)
		);
	}

	/**
	 * Returns a promise that:
	 * - resolves to 'true' when navigation succeeds,
	 * - resolves to 'false' when navigation fails,
	 * - is rejected when an error happens.
	 */
	navigateByUrl(payload: NavigateByUrlPayload): Promise<boolean> {
		return this.router.navigateByUrl(this.location.normalize(payload.url), payload.extras);
	}

	getCurrentExternalUrl() {
		return this.location.prepareExternalUrl(this.router.url.toString());
	}

	/**
	 * Returns the route config synchronously.
	 * Returns `undefined` if not found, does not trigger lazy-loading.
	 */
	getRouteConfig(routeName: string): RouteConfig | undefined {
		const config = this.routeLookup[routeName];

		if (config) {
			return config;
		}

		const moduleName = this.getModuleName(routeName, config);
		const moduleLoaded = this.lazyLoadingService.getModuleStatus(moduleName) === FetchStatusType.Success;
		if (moduleLoaded || !this.routeLookup[moduleName]) {
			this.logger.warn("getRouteConfig", `Config not found by name: ${routeName}`);
		}

		return undefined;
	}

	/**
	 * Returns the route config. If route is not yet loaded, it triggers lazy-loading.
	 */
	getRouteConfig$(routeName: string): Observable<RouteConfig | undefined> {
		const routeConfig = this.getRouteConfig(routeName);

		// if there is `loadChildren` property, route was not yet fetched
		if (routeConfig && !routeConfig.loadChildren) {
			return of(routeConfig);
		}

		const lazyModuleName = this.getModuleName(routeName, routeConfig);
		if (!routeConfig && !this.routeLookup[lazyModuleName]) {
			this.logger.warn("getRouteConfig$", `Cannot trigger lazy-loading of: ${routeName}`);
			return of(undefined);
		}

		return this.loadModule(lazyModuleName).pipe(
			filter((status) => status === FetchStatusType.Success),
			take(1),
			map(() => this.getRouteConfig(routeName))
		);
	}

	getConfigFromStateSnapshot(snapshot: RouterStateSnapshot): RouteConfig | undefined {
		const activated = getActivatedRouteSnapshot(snapshot);
		return activated ? (activated.routeConfig as RouteConfig) : undefined;
	}

	getParentNames(routeName: string): string[] {
		const routeConfig = this.getRouteConfig(routeName);

		if (!routeConfig) {
			return [];
		}

		const splittedName = routeConfig.name.split(".");
		let parents: string[] = [];

		if (splittedName.length > 1) {
			parents = [splittedName[0]];
		}

		return uniq([
			...parents,
			...getAllParents(routeConfig, this.routeLookup).map((parentRoute) => parentRoute.name),
		]);
	}

	generateUrl(payload: NavigatePayload, prepareExternalUrl = true): string {
		const commands = this.convertToCommands(payload.routeName, payload.params);
		if (!commands) {
			return "";
		}
		const url = this.router.createUrlTree(commands, payload.extras).toString();
		if (!prepareExternalUrl) {
			return url;
		}

		const externalUrl = this.location.prepareExternalUrl(url);

		// For language prefixed URLs (e.g., /en/, /tr/), remove trailing slash
		const langMatch = externalUrl.match(/^\/[a-z]{2}\/$/i);
		if (langMatch) {
			return externalUrl.slice(0, -1);
		}

		return externalUrl;
	}

	generateUrl$(payload: NavigatePayload, prepareExternalUrl = true): Observable<string> {
		const url = this.generateUrl(payload, prepareExternalUrl);

		if (url) {
			return of(url);
		}

		return this.lazyLoadingService.getLazyLoadSuccess$().pipe(
			filter(({ parentRoute }) => startsWith(payload.routeName, parentRoute.name)),
			map(() => this.generateUrl(payload, prepareExternalUrl)),
			ignoreEmpty(),
			take(1)
		);
	}

	generateUrlFromSnapshot(snapshot: ActivatedRouteSnapshot, prepareExternalUrl = true): string {
		return this.generateUrl(
			{
				routeName: (snapshot.routeConfig as RouteConfig).name,
				params: snapshot.params,
				extras: {
					queryParams: snapshot.queryParams,
					fragment: snapshot.fragment!,
				},
			},
			prepareExternalUrl
		);
	}

	updateRoute(routeName: string, newRoute: Partial<RouteConfig>): boolean {
		const currentRoute = this.getRouteConfig(routeName);
		if (!currentRoute) {
			return false;
		}

		const { name, path } = currentRoute;
		newRoute = { ...newRoute, name, path };

		let hasUpdated = false;
		this.router.config.map((route, index) => {
			if (route.path === newRoute.path) {
				this.router.config[index] = newRoute;
				hasUpdated = true;
			}
			return route;
		});
		return hasUpdated;
	}

	loadModule(name: string): Observable<FetchStatusType | undefined> {
		const config = this.getRouteConfig(name);
		if (!config) {
			this.logger.warn("loadModule", `Module ${name} is not found and cannot be loaded`);
			return of(FetchStatusType.Fail);
		}
		return this.lazyLoadingService.loadModule(config);
	}

	replacePath(path: string) {
		this.location.replaceState(this.location.normalize(path));
	}

	pushPath(path: string) {
		this.location.go(this.location.normalize(path));
	}

	goBack() {
		this.location.back();
	}

	onHashChange$() {
		return new Observable((observer) =>
			this.platformLocation.onHashChange((locationChangeEvent) => observer.next(locationChangeEvent))
		);
	}

	onPopState$() {
		return new Observable((observer) =>
			this.platformLocation.onPopState((locationChangeEvent) => observer.next(locationChangeEvent))
		);
	}

	private walkRouteConfig(
		routes: Route[],
		callback: (config: RouteConfig, parent?: RouteConfig) => void,
		parent?: RouteConfig
	) {
		const loadedRoutes = [...routes, ...((parent as any)?._loadedRoutes || [])];
		for (const route of loadedRoutes) {
			callback(route as RouteConfig, parent);
			if (route._loadedRoutes) {
				this.walkRouteConfig(route._loadedRoutes, callback, route as RouteConfig);
			}
		}
	}

	convertToCommands(routeName: string, params?: any): any[] | undefined {
		const routes: RouteConfig[] = [];
		let config = this.getRouteConfig(routeName);

		if (!config) {
			return undefined;
		}

		while (config) {
			routes.unshift(config);
			config = this.parentLookup[config.name];
		}

		return lodashMap(
			flatMap(routes, (route) => route.path!.replace(/^\//, "").split("/")),
			(segment) => {
				if (segment.startsWith(":")) {
					const param = params[segment.substring(1)];
					if (isNil(param)) {
						throw new Error(`Value for router param '${segment}' not found.`);
					}
					return param;
				}
				if (segment === "**") {
					return "";
				}
				return segment;
			}
		);
	}

	private getModuleName(routeName: string, routeConfig?: RouteConfig): string {
		return routeConfig?.data?.module ?? routeName.split(".")[0];
	}
}
